import { PagingParameters } from "@/api/dtos";
import { readTablePreferences } from "@/hooks/useTablePreferences";
import {
  useDeleteComplexLink,
  useGetComplexRelationFields,
  useGetNotRelatedResource,
} from "@/api/related-fields";
import {
  artworkColumnsFormatted,
  artworkColumnsVisibility,
  getArtworkForComplexLink,
} from "@/config/dashboard/artwork";
import { publicAuctionColumnsVisibility } from "@/config/dashboard/public-auction";
import { generateTable } from "@/config/dashboard/general";
import { FormLayoutElement } from "@/config/forms/general";
import { getMainLanguage, SupportedLocale } from "@/i18n";
import { Artwork, ArtworkInAuction, Auction } from "@/lib/types";
import { parseJsonOrUndefined } from "@/lib/utils";
import { ColumnDef, Row, VisibilityState } from "@tanstack/react-table";
import {
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { LinkDrawerRowActions } from "@/components/table/LinkDrawerRowActions";
import { createActionsColumnDef } from "@/components/table/table-actions-column";
import { LinkableType } from "../LinksManagement";
import { HtmlCell, ImageCell, TextCell } from "../table/CustomCell";
import { DataTable } from "../ui/datatable";
import { Input } from "../ui/input";
import ArtworkInAuctionDrawer from "./drawer/ArtworkInAuctionDrawer";
import { buildElColumns } from "./elColumnComposer";
import { RelatedLinksListGridLayout } from "./RelatedLinksListGridLayout";
import {
  ArtworkLinkGridCard,
  AuctionLinkGridCard,
} from "./relatedLinkGridCards";
import { GridCardWithActions } from "./GridCardWithActions";

interface ComplexLinkProps {
  id: string;
  artwork: Artwork;
  fields: FormLayoutElement[] | undefined;
  type: LinkableType;
  auction: Auction;
}

type LegacyMoneyValue = {
  amount?: string | number;
  currency?: string;
};

const formatLegacyMoneyValue = (value: unknown): string => {
  if (value == null || value === "") return "";
  const parsed =
    typeof value === "string"
      ? parseJsonOrUndefined<LegacyMoneyValue>(value)
      : (value as LegacyMoneyValue);

  if (!parsed?.amount) return "";
  const amount =
    typeof parsed.amount === "number"
      ? parsed.amount
      : Number(parsed.amount.replace(",", "."));
  if (Number.isNaN(amount)) return "";

  const currency = parsed.currency?.toUpperCase();
  if (!currency) return amount.toFixed(2);

  try {
    return new Intl.NumberFormat(getMainLanguage() as SupportedLocale, {
      style: "currency",
      currency,
    }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${currency}`;
  }
};

const columnsAuction = [
  {
    accessorKey: "auction.ventesDescriptionNumeroVente",
    header: "ventesDescriptionNumeroVente",
    cell: ({ row }: { row: Row<ArtworkInAuction> }) => {
      return (
        <TextCell value={row.original.auction.ventesDescriptionNumeroVente} />
      );
    },
  },
  {
    accessorKey: "ventesDescriptionVignette",
    header: "",
    size: 100,
    cell: ({ row }: { row: Row<ArtworkInAuction> }) => {
      return (
        <ImageCell
          value={""}
          attachments={
            row.original.auction?.attachments ??
            row.original.attachments ??
            []
          }
          domain={"auction"}
        />
      );
    },
  },
  {
    accessorKey: "auction.ventesDescriptionCommissaires",
    header: "ventesDescriptionCommissaires",
    cell: ({ row }: { row: Row<ArtworkInAuction> }) => {
      return (
        <TextCell value={row.original.auction.ventesDescriptionCommissaires} />
      );
    },
  },
  {
    accessorKey: "auction.ventesDescriptionDate",
    header: "ventesDescriptionDate",
    cell: ({ row }: { row: Row<ArtworkInAuction> }) => {
      return <TextCell value={row.original.auction.ventesDescriptionDate} />;
    },
  },
  {
    accessorKey: "auction.ventesDescriptionVille",
    header: "ventesDescriptionVille",
    cell: ({ row }: { row: Row<ArtworkInAuction> }) => {
      return <TextCell value={row.original.auction.ventesDescriptionVille} />;
    },
  },
  {
    accessorKey: "auction.ventesDescriptionPays",
    header: "ventesDescriptionPays",
    cell: ({ row }: { row: Row<ArtworkInAuction> }) => {
      return <TextCell value={row.original.auction.ventesDescriptionPays} />;
    },
  },
];
/**
 * ArtworkLinks component
 * @returns {JSX.Element}
 */
const ArtworkInAuctionLinks = ({
  id,
  artwork,
  fields,
  type,
  auction,
}: ComplexLinkProps) => {
  const { t } = useTranslation();

  const { data: relatedData } = useGetComplexRelationFields(
    id,
    "artworkInAuctions",
    type,
  );

  const [searchContent, setSearchContent] = useState("");

  const { mutate: deleteArtworkInAuction } = useDeleteComplexLink(
    id,
    "artworkInAuctions",
    type,
  );

  const [isAddMode, setIsAddMode] = useState(false);

  const handleDeleteRelation = useCallback(
    async (relationId: string) => {
      await deleteArtworkInAuction({
        id: relationId,
        relation: "artworkInAuction",
      });
    },
    [deleteArtworkInAuction],
  );

  const renderArtworkInAuctionRowActions = useCallback(
    (data: ArtworkInAuction) => {
      const preview =
        type === "auction"
          ? {
              previewType: "artwork" as const,
              previewId: data.artwork.id,
              previewElements: [
                {
                  label: t("oeuvresDescriptionTitre"),
                  value: data.artwork.oeuvresDescriptionTitre,
                  type: "text" as const,
                },
                {
                  label: t("oeuvresDescriptionNumeroEnregistrement"),
                  value: data.artwork.oeuvresDescriptionNumeroEnregistrement,
                  type: "text" as const,
                },
                {
                  label: t("oeuvresDescriptionResume"),
                  value: data.artwork.oeuvresDescriptionResume,
                  type: "richtext" as const,
                },
                {
                  label: t("attachments"),
                  value: data.artwork.attachments,
                  type: "image" as const,
                },
              ],
            }
          : type === "artwork"
            ? {
                previewType: "auction" as const,
                previewId: data.auction.id,
                previewElements: [
                  {
                    label: t("attachments"),
                    value: data.auction?.attachments ?? [],
                    type: "image" as const,
                  },
                  {
                    label: t("ventesDescriptionNumeroVente"),
                    value: data.auction.ventesDescriptionNumeroVente,
                    type: "text" as const,
                  },
                  {
                    label: t("ventesDescriptionResume"),
                    value: data.auction.ventesDescriptionResume,
                    type: "richtext" as const,
                  },
                ],
              }
            : null;

      if (!preview) return null;

      return (
        <LinkDrawerRowActions
          previewType={preview.previewType}
          previewId={preview.previewId}
          previewElements={preview.previewElements}
          onDelete={() => handleDeleteRelation(data.id)}
          editDrawer={({ open, onOpenChange }) => (
            <ArtworkInAuctionDrawer
              key={data.id}
              fields={fields}
              data={data}
              queryId={id}
              type={type}
              open={open}
              onOpenChange={onOpenChange}
            />
          )}
        />
      );
    },
    [fields, handleDeleteRelation, id, t, type],
  );

  const columns: ColumnDef<ArtworkInAuction, any>[] = useMemo(() => {
    const columnsArtwork = getArtworkForComplexLink() as ColumnDef<
      ArtworkInAuction,
      any
    >[];
    const targetColumns =
      type === "artwork"
        ? (columnsAuction.map((column) => ({
            ...column,
            header: typeof column.header === "string" ? t(column.header) : "",
          })) as ColumnDef<ArtworkInAuction, any>[])
        : columnsArtwork.map((column) => ({
            ...column,
            header: typeof column.header === "string" ? t(column.header) : "",
          }));

    const transitionColumns: ColumnDef<ArtworkInAuction, any>[] = [
      {
        accessorKey: "oeuvresDansVentesDescriptionNumeroLot",
        header: t("auctionLotNumber"),
      },
      {
        accessorKey: "oeuvresDansVentesDescriptionReproduitPage",
        header: t("oeuvresDansVentesDescriptionReproduitPage"),
      },
      {
        accessorKey: "oeuvresDansVentesDescriptionEstimationHaute",
        header: t("oeuvresDansVentesDescriptionEstimationHaute"),
        cell: ({ row }: { row: Row<ArtworkInAuction> }) => (
          <TextCell
            value={formatLegacyMoneyValue(
              row.original.oeuvresDansVentesDescriptionEstimationHaute,
            )}
          />
        ),
      },
      {
        accessorKey: "oeuvresDansVentesDescriptionEstimationBasse",
        header: t("oeuvresDansVentesDescriptionEstimationBasse"),
        cell: ({ row }: { row: Row<ArtworkInAuction> }) => (
          <TextCell
            value={formatLegacyMoneyValue(
              row.original.oeuvresDansVentesDescriptionEstimationBasse,
            )}
          />
        ),
      },
      {
        accessorKey: "oeuvresDansVentesDescriptionMiseAPrix",
        header: t("oeuvresDansVentesDescriptionMiseAPrix"),
        cell: ({ row }: { row: Row<ArtworkInAuction> }) => (
          <TextCell
            value={formatLegacyMoneyValue(
              row.original.oeuvresDansVentesDescriptionMiseAPrix,
            )}
          />
        ),
      },
      {
        accessorKey: "oeuvresDansVentesDescriptionPrixMarteau",
        header: t("oeuvresDansVentesDescriptionPrixMarteau"),
        cell: ({ row }: { row: Row<ArtworkInAuction> }) => (
          <TextCell
            value={formatLegacyMoneyValue(
              row.original.oeuvresDansVentesDescriptionPrixMarteau,
            )}
          />
        ),
      },
      {
        accessorKey: "oeuvresDansVentesDescriptionPrixAvecFrais",
        header: t("oeuvresDansVentesDescriptionPrixAvecFrais"),
        cell: ({ row }: { row: Row<ArtworkInAuction> }) => (
          <TextCell
            value={formatLegacyMoneyValue(
              row.original.oeuvresDansVentesDescriptionPrixAvecFrais,
            )}
          />
        ),
      },
      {
        accessorKey: "oeuvresDansVentesDescriptionVendu",
        header: t("oeuvresDansVentesDescriptionVendu"),
        cell: ({ row }: { row: Row<ArtworkInAuction> }) => (
          <TextCell value={row.original.oeuvresDansVentesDescriptionVendu ? t("yes") : t("no")} />
        ),
      },
      {
        accessorKey: "oeuvresDansVentesDescriptionResultatNonListe",
        header: t("oeuvresDansVentesDescriptionResultatNonListe"),
        cell: ({ row }: { row: Row<ArtworkInAuction> }) => (
          <TextCell
            value={
              row.original.oeuvresDansVentesDescriptionResultatNonListe
                ? t("yes")
                : t("no")
            }
          />
        ),
      },
      {
        accessorKey: "oeuvresDansVentesDescriptionNonVenu",
        header: t("oeuvresDansVentesDescriptionNonVenu"),
        cell: ({ row }: { row: Row<ArtworkInAuction> }) => (
          <TextCell value={row.original.oeuvresDansVentesDescriptionNonVenu ? t("yes") : t("no")} />
        ),
      },
      {
        accessorKey: "oeuvresDansVentesDescriptionNonVendu",
        header: t("oeuvresDansVentesDescriptionNonVendu"),
        cell: ({ row }: { row: Row<ArtworkInAuction> }) => (
          <TextCell value={row.original.oeuvresDansVentesDescriptionNonVendu ? t("yes") : t("no")} />
        ),
      },
      {
        accessorKey: "oeuvresDansVentesDescriptionNonReproduit",
        header: t("oeuvresDansVentesDescriptionNonReproduit"),
        cell: ({ row }: { row: Row<ArtworkInAuction> }) => (
          <TextCell
            value={row.original.oeuvresDansVentesDescriptionNonReproduit ? t("yes") : t("no")}
          />
        ),
      },
      {
        accessorKey: "oeuvresDansVentesDescriptionRetire",
        header: t("oeuvresDansVentesDescriptionRetire"),
        cell: ({ row }: { row: Row<ArtworkInAuction> }) => (
          <TextCell value={row.original.oeuvresDansVentesDescriptionRetire ? t("yes") : t("no")} />
        ),
      },
      {
        accessorKey: "oeuvresDansVentesDescriptionNotes",
        header: t("oeuvresDansVentesDescriptionNotes"),
        cell: ({ row }: { row: Row<ArtworkInAuction> }) => (
          <HtmlCell value={row.original.oeuvresDansVentesDescriptionNotes ?? ""} />
        ),
      },
    ];

    const actionsColumn = createActionsColumnDef<ArtworkInAuction>(({ row }) =>
      renderArtworkInAuctionRowActions(row.original),
    );
    return buildElColumns(targetColumns, transitionColumns, actionsColumn);
  }, [renderArtworkInAuctionRowActions, t, type]);

  const datatableConf = useMemo(() => {
    const layoutBackLabel = t("relatedRecords");
    const searchInput = (
      <div className="flex items-center gap-2">
        <Input
          placeholder={t("search")}
          className="w-80"
          value={searchContent}
          onChange={(e) => setSearchContent(e.target.value)}
        />
      </div>
    );
    if (type === "artwork") {
      return (
        <ArtworkToAuctionLinks
          id={id}
          relatedData={relatedData}
          fields={fields}
          artwork={artwork}
          isAddMode={isAddMode}
          columnsLink={columns}
          renderRowActions={renderArtworkInAuctionRowActions}
          searchContent={searchContent}
          searchInput={searchInput}
          listGridTitle={t("auctionsRelated")}
          layoutBackLabel={layoutBackLabel}
          setIsAddMode={setIsAddMode}
        />
      );
    }
    return (
      <AuctionToArtworkLinks
        id={id}
        relatedData={relatedData}
        fields={fields}
        auction={auction}
        isAddMode={isAddMode}
        columnsLink={columns}
        renderRowActions={renderArtworkInAuctionRowActions}
        searchContent={searchContent}
        searchInput={searchInput}
        listGridTitle={t("artworksRelated")}
        layoutBackLabel={layoutBackLabel}
        setIsAddMode={setIsAddMode}
      />
    );
  }, [
    type,
    id,
    relatedData,
    fields,
    artwork,
    isAddMode,
    columns,
    searchContent,
    setSearchContent,
    auction,
    t,
  ]);

  return <div className="w-full">{datatableConf}</div>;
};

export type LinksTableProps<T> = {
  id: string;
  relatedData: T[];
  fields: FormLayoutElement[] | undefined;
  isAddMode: boolean;
  columnsLink: ColumnDef<T, any>[];
  renderRowActions?: (row: T) => ReactNode;
  searchContent: string;
  /** Search field for add mode (controlled by parent). */
  searchInput?: ReactNode;
  listGridTitle?: ReactNode;
  layoutBackLabel?: ReactNode;
  setIsAddMode?: Dispatch<SetStateAction<boolean>>;
};

type ArtworkToAuctionLinksProps = LinksTableProps<ArtworkInAuction> & {
  artwork: Artwork | undefined;
};

type AuctionToArtworkLinksProps = LinksTableProps<ArtworkInAuction> & {
  auction: Auction | undefined;
};

//Composant pour Oeuvres
const ArtworkToAuctionLinks = ({
  id,
  relatedData,
  fields,
  artwork,
  isAddMode,
  columnsLink,
  renderRowActions,
  searchContent,
  searchInput,
  listGridTitle,
  layoutBackLabel,
  setIsAddMode,
}: ArtworkToAuctionLinksProps) => {
  const { t } = useTranslation();
  const [selectedRelationId, setSelectedRelationId] = useState<string | null>(
    null,
  );
  const [getPagination, setPagination] = useState<PagingParameters>({
    page: 0,
    pageSize: 10,
  });
  const [relatedPagination, setRelatedPagination] = useState<PagingParameters>({
    page: 0,
    pageSize: 10,
  });

  useEffect(() => {
    setPagination((prev) => ({ ...prev, page: 0 }));
    setRelatedPagination((prev) => ({ ...prev, page: 0 }));
  }, [isAddMode]);

  const STORAGE_KEY_ADD = "artwork-links-auction-add";
  const STORAGE_KEY_LINKED = "artwork-links-auction-linked";
  const defaultVisibilityAuction = {
    ...publicAuctionColumnsVisibility,
    vignette: true,
  };
  const [columnVisibilityAdd, setColumnVisibilityAdd] =
    useState<VisibilityState>(() => {
      try {
        const saved = readTablePreferences(STORAGE_KEY_ADD);
        if (saved) {
          const { visibility } = saved;
          if (visibility && typeof visibility === "object")
            return { ...defaultVisibilityAuction, ...visibility };
        }
      } catch {
        // ignore
      }
      return { ...defaultVisibilityAuction };
    });
  const [columnVisibilityLinked, setColumnVisibilityLinked] =
    useState<VisibilityState>(() => {
      try {
        const saved = readTablePreferences(STORAGE_KEY_LINKED);
        if (saved) {
          const { visibility } = saved;
          if (visibility && typeof visibility === "object")
            return { ...defaultVisibilityAuction, ...visibility };
        }
      } catch {
        // ignore
      }
      return { ...defaultVisibilityAuction };
    });
  const [columnsOrderAdd, setColumnsOrderAdd] = useState<string[]>(() => {
    try {
      const saved = readTablePreferences(STORAGE_KEY_ADD);
      if (saved) {
        const { order, columnOrder } = saved;
        const storedOrder = Array.isArray(order)
          ? order
          : Array.isArray(columnOrder)
            ? columnOrder
            : null;
        if (Array.isArray(storedOrder)) {
          const normalizedOrder = [...new Set(storedOrder)] as string[];
          return normalizedOrder.includes("actions")
            ? [...normalizedOrder.filter((id) => id !== "actions"), "actions"]
            : normalizedOrder;
        }
      }
    } catch {
      // ignore
    }
    return [];
  });
  const [columnsOrderLinked, setColumnsOrderLinked] = useState<string[]>(() => {
    try {
      const saved = readTablePreferences(STORAGE_KEY_LINKED);
      if (saved) {
        const { order, columnOrder } = saved;
        const storedOrder = Array.isArray(order)
          ? order
          : Array.isArray(columnOrder)
            ? columnOrder
            : null;
        if (Array.isArray(storedOrder)) {
          const normalizedOrder = [...new Set(storedOrder)] as string[];
          return normalizedOrder.includes("actions")
            ? [...normalizedOrder.filter((id) => id !== "actions"), "actions"]
            : normalizedOrder;
        }
      }
    } catch {
      // ignore
    }
    return [];
  });

  const { data: notAlreadyRelatedAuctions } = useGetNotRelatedResource<Auction>(
    {
      resultType: "auction",
      resourceId: id,
      pagination: getPagination,
      searchQuery: searchContent,
    },
  );

  const columnsAuctions: ColumnDef<Auction, any>[] = useMemo(() => {
    return [
      {
        accessorKey: "ventesDescriptionNumeroVente",
        header: t("ventesDescriptionNumeroVente"),
        cell: ({ row }) => {
          return <TextCell value={row.original.ventesDescriptionNumeroVente} />;
        },
      },
      {
        accessorKey: "ventesDescriptionVignette",
        header: "",
        size: 100,
        cell: ({ row }: { row: Row<Auction> }) => {
          return (
            <ImageCell
              value={""}
              attachments={row.original.attachments ?? []}
              domain={"auction"}
            />
          );
        },
      },
      {
        accessorKey: "ventesDescriptionTitre",
        header: t("ventesDescriptionTitre"),
        size: 500,
        cell: (props) => {
          return <HtmlCell value={String(props.getValue() ?? "")} />;
        },
      },
      {
        accessorKey: "ventesDescriptionDate",
        header: t("ventesDescriptionDate"),
        size: 100,
        cell: ({ row }) => {
          return <TextCell value={row.original.ventesDescriptionDate} />;
        },
      },
      {
        accessorKey: "ventesDescriptionVille",
        header: t("ventesDescriptionVille"),
        cell: ({ row }) => {
          return <TextCell value={row.original.ventesDescriptionVille} />;
        },
      },
      {
        accessorKey: "ventesDescriptionPays",
        header: t("ventesDescriptionPays"),
        cell: ({ row }) => {
          return <TextCell value={row.original.ventesDescriptionPays} />;
        },
      },
      createActionsColumnDef<Auction>(({ row }) => {
          const data = row.original as Auction;
          if (!artwork) return null;
          return (
            <LinkDrawerRowActions
              previewType="auction"
              previewId={row.original.id}
              previewElements={[
                {
                  label: t("attachments"),
                  value: row.original.attachments ?? [],
                  type: "image",
                },
                {
                  label: t("ventesDescriptionNumeroVente"),
                  value: row.original.ventesDescriptionNumeroVente,
                  type: "text",
                },
                {
                  label: t("ventesDescriptionResume"),
                  value: row.original.ventesDescriptionResume,
                  type: "richtext",
                },
              ]}
              editLabel={t("actions.add")}
              editDrawer={({ open, onOpenChange }) => (
                <ArtworkInAuctionDrawer
                  fields={fields}
                  key={row.original.id}
                  queryId={artwork.id}
                  type={"artwork"}
                  open={open}
                  onOpenChange={onOpenChange}
                  data={{
                    id: "",
                    auction: data,
                    artwork: artwork,
                    oeuvresDansVentesDescriptionNumeroLot: "",
                    oeuvresDansVentesDescriptionNotes: "",
                    oeuvresDansVentesDescriptionReproduitPage: "",
                    oeuvresDansVentesDescriptionEstimationHaute: "",
                    oeuvresDansVentesDescriptionEstimationBasse: "",
                    oeuvresDansVentesDescriptionVendu: false,
                    oeuvresDansVentesDescriptionResultatNonListe: false,
                    oeuvresDansVentesDescriptionNonVenu: false,
                    oeuvresDansVentesDescriptionNonVendu: false,
                    oeuvresDansVentesDescriptionNonReproduit: false,
                    oeuvresDansVentesDescriptionRetire: false,
                    oeuvresDansVentesDescriptionMiseAPrix: "",
                    oeuvresDansVentesDescriptionPrixMarteau: "",
                    oeuvresDansVentesDescriptionPrixAvecFrais: "",
                  }}
                />
              )}
            />
          );
        }),
    ];
  }, [artwork, fields, id, t]);

  const paginatedRelatedData = useMemo(() => {
    const start = relatedPagination.page * relatedPagination.pageSize;
    const end = start + relatedPagination.pageSize;
    return (relatedData ?? []).slice(start, end);
  }, [relatedData, relatedPagination.page, relatedPagination.pageSize]);

  const relatedPaginationProps = {
    pagination: relatedPagination,
    setPage: setRelatedPagination,
    totalSize: relatedData?.length ?? 0,
  };

  const tableOptionsAdd = {
    enableColumnResizing: true,
    onColumnVisibilityChange: setColumnVisibilityAdd,
    onColumnOrderChange: setColumnsOrderAdd,
    state: {
      columnVisibility: columnVisibilityAdd,
      columnOrder: columnsOrderAdd,
    },
  };

  const tableOptionsLinked = {
    enableColumnResizing: true,
    onColumnVisibilityChange: setColumnVisibilityLinked,
    onColumnOrderChange: setColumnsOrderLinked,
    state: {
      columnVisibility: columnVisibilityLinked,
      columnOrder: columnsOrderLinked,
    },
  };

  const addModeTable = (
    <DataTable
      columns={columnsAuctions}
      data={notAlreadyRelatedAuctions?.data ?? []}
      pagination={{
        pagination: getPagination,
        setPage: setPagination,
        totalSize: notAlreadyRelatedAuctions?.totalSize ?? 0,
      }}
      storageKey={STORAGE_KEY_ADD}
      options={tableOptionsAdd}
    />
  );

  const listTable = (
    <DataTable
      columns={columnsLink}
      data={paginatedRelatedData}
      pagination={relatedPaginationProps}
      storageKey={STORAGE_KEY_LINKED}
      options={tableOptionsLinked}
    />
  );

  if (
    listGridTitle != null &&
    setIsAddMode != null &&
    searchInput != null
  ) {
    return (
      <RelatedLinksListGridLayout
        storageKey={STORAGE_KEY_LINKED}
        title={listGridTitle}
        backLabel={layoutBackLabel}
        isAddMode={isAddMode}
        onBack={() => setIsAddMode(false)}
        onToggleAdd={() => setIsAddMode(true)}
        searchInput={searchInput}
        addModeTable={addModeTable}
        listTable={listTable}
        gridContent={
          <>
            {paginatedRelatedData.map((row) => (
              <div key={row.id}>
                <GridCardWithActions actions={renderRowActions?.(row)}>
                  <AuctionLinkGridCard
                    item={row.auction}
                    t={t}
                    onTextClick={() => setSelectedRelationId(row.id)}
                  />
                </GridCardWithActions>
                <ArtworkInAuctionDrawer
                  fields={fields}
                  data={row}
                  queryId={id}
                  type="artwork"
                  open={selectedRelationId === row.id}
                  onOpenChange={(open) => {
                    if (!open) setSelectedRelationId(null);
                  }}
                />
              </div>
            ))}
          </>
        }
        relatedPagination={relatedPaginationProps}
      />
    );
  }

  return isAddMode ? addModeTable : listTable;
};

//Composant pour ventes
const AuctionToArtworkLinks = ({
  id,
  relatedData,
  fields,
  auction,
  isAddMode,
  columnsLink,
  renderRowActions,
  searchContent,
  searchInput,
  listGridTitle,
  layoutBackLabel,
  setIsAddMode,
}: AuctionToArtworkLinksProps) => {
  const [selectedRelationId, setSelectedRelationId] = useState<string | null>(
    null,
  );
  const [getPagination, setPagination] = useState<PagingParameters>({
    page: 0,
    pageSize: 10,
  });
  const [relatedPagination, setRelatedPagination] = useState<PagingParameters>({
    page: 0,
    pageSize: 10,
  });

  useEffect(() => {
    setPagination((prev) => ({ ...prev, page: 0 }));
    setRelatedPagination((prev) => ({ ...prev, page: 0 }));
  }, [isAddMode]);

  const STORAGE_KEY_ADD_AUCTION = "artwork-links-auction-to-artwork-add";
  const STORAGE_KEY_LINKED_AUCTION = "artwork-links-auction-to-artwork-linked";
  const defaultVisibilityArtworks = {
    ...artworkColumnsVisibility,
    oeuvresDescriptionVignette: true,
    oeuvresPublicationStatus: false,
  };
  const [columnVisibilityAdd, setColumnVisibilityAdd] =
    useState<VisibilityState>(() => {
      try {
        const saved = readTablePreferences(STORAGE_KEY_ADD_AUCTION);
        if (saved) {
          const { visibility } = saved;
          if (visibility && typeof visibility === "object")
            return { ...defaultVisibilityArtworks, ...visibility };
        }
      } catch {
        // ignore
      }
      return { ...defaultVisibilityArtworks };
    });
  const [columnVisibilityLinked, setColumnVisibilityLinked] =
    useState<VisibilityState>(() => {
      try {
        const saved = readTablePreferences(STORAGE_KEY_LINKED_AUCTION);
        if (saved) {
          const { visibility } = saved;
          if (visibility && typeof visibility === "object")
            return { ...defaultVisibilityArtworks, ...visibility };
        }
      } catch {
        // ignore
      }
      return { ...defaultVisibilityArtworks };
    });
  const [columnsOrderAdd, setColumnsOrderAdd] = useState<string[]>(() => {
    try {
      const saved = readTablePreferences(STORAGE_KEY_ADD_AUCTION);
      if (saved) {
        const { order, columnOrder } = saved;
        const storedOrder = Array.isArray(order)
          ? order
          : Array.isArray(columnOrder)
            ? columnOrder
            : null;
        if (Array.isArray(storedOrder)) {
          const normalizedOrder = [...new Set(storedOrder)] as string[];
          return normalizedOrder.includes("actions")
            ? [...normalizedOrder.filter((id) => id !== "actions"), "actions"]
            : normalizedOrder;
        }
      }
    } catch {
      // ignore
    }
    return [];
  });
  const [columnsOrderLinked, setColumnsOrderLinked] = useState<string[]>(() => {
    try {
      const saved = readTablePreferences(STORAGE_KEY_LINKED_AUCTION);
      if (saved) {
        const { order, columnOrder } = saved;
        const storedOrder = Array.isArray(order)
          ? order
          : Array.isArray(columnOrder)
            ? columnOrder
            : null;
        if (Array.isArray(storedOrder)) {
          const normalizedOrder = [...new Set(storedOrder)] as string[];
          return normalizedOrder.includes("actions")
            ? [...normalizedOrder.filter((id) => id !== "actions"), "actions"]
            : normalizedOrder;
        }
      }
    } catch {
      // ignore
    }
    return [];
  });

  const { data: notAlreadyRelatedArtworks } = useGetNotRelatedResource<Artwork>(
    {
      resultType: "artwork",
      resourceId: id,
      pagination: getPagination,
      searchQuery: searchContent,
    },
  );

  const { t } = useTranslation();
  const columnsArtworks: ColumnDef<Artwork, any>[] = useMemo(() => {
    return [
      ...generateTable(artworkColumnsFormatted, "artwork"),
      createActionsColumnDef<Artwork>(({ row }) => {
          const data = row.original as Artwork;
          if (!auction) return null;
          return (
            <LinkDrawerRowActions
              previewType="artwork"
              previewId={row.original.id}
              previewElements={[
                {
                  label: t("oeuvresDescriptionTitre"),
                  value: row.original.oeuvresDescriptionTitre,
                  type: "text",
                },
                {
                  label: t("oeuvresDescriptionNumeroEnregistrement"),
                  value: row.original.oeuvresDescriptionNumeroEnregistrement,
                  type: "text",
                },
                {
                  label: t("oeuvresDescriptionResume"),
                  value: row.original.oeuvresDescriptionResume,
                  type: "richtext",
                },
                {
                  label: t("attachments"),
                  value: row.original.attachments,
                  type: "image",
                },
              ]}
              editLabel={t("actions.add")}
              editDrawer={({ open, onOpenChange }) => (
                <ArtworkInAuctionDrawer
                  fields={fields}
                  key={row.original.id}
                  queryId={auction.id}
                  type={"auction"}
                  open={open}
                  onOpenChange={onOpenChange}
                  data={{
                    id: "",
                    auction: auction,
                    artwork: data,
                    oeuvresDansVentesDescriptionNumeroLot: "",
                    oeuvresDansVentesDescriptionNotes: "",
                    oeuvresDansVentesDescriptionReproduitPage: "",
                    oeuvresDansVentesDescriptionEstimationHaute: "",
                    oeuvresDansVentesDescriptionEstimationBasse: "",
                    oeuvresDansVentesDescriptionVendu: false,
                    oeuvresDansVentesDescriptionResultatNonListe: false,
                    oeuvresDansVentesDescriptionNonVenu: false,
                    oeuvresDansVentesDescriptionNonVendu: false,
                    oeuvresDansVentesDescriptionNonReproduit: false,
                    oeuvresDansVentesDescriptionRetire: false,
                    oeuvresDansVentesDescriptionMiseAPrix: "",
                    oeuvresDansVentesDescriptionPrixMarteau: "",
                    oeuvresDansVentesDescriptionPrixAvecFrais: "",
                  }}
                />
              )}
            />
          );
        }),
    ];
  }, [auction, fields, t]);

  const paginatedRelatedData = useMemo(() => {
    const start = relatedPagination.page * relatedPagination.pageSize;
    const end = start + relatedPagination.pageSize;
    return (relatedData ?? []).slice(start, end);
  }, [relatedData, relatedPagination.page, relatedPagination.pageSize]);

  const relatedPaginationProps = {
    pagination: relatedPagination,
    setPage: setRelatedPagination,
    totalSize: relatedData?.length ?? 0,
  };

  const tableOptionsAddAuc = {
    enableColumnResizing: true,
    onColumnVisibilityChange: setColumnVisibilityAdd,
    onColumnOrderChange: setColumnsOrderAdd,
    state: {
      columnVisibility: columnVisibilityAdd,
      columnOrder: columnsOrderAdd,
    },
  };

  const tableOptionsLinkedAuc = {
    enableColumnResizing: true,
    onColumnVisibilityChange: setColumnVisibilityLinked,
    onColumnOrderChange: setColumnsOrderLinked,
    state: {
      columnVisibility: columnVisibilityLinked,
      columnOrder: columnsOrderLinked,
    },
  };

  const addModeTable = (
    <DataTable
      columns={columnsArtworks}
      data={notAlreadyRelatedArtworks?.data ?? []}
      pagination={{
        pagination: getPagination,
        setPage: setPagination,
        totalSize: notAlreadyRelatedArtworks?.totalSize ?? 0,
      }}
      storageKey={STORAGE_KEY_ADD_AUCTION}
      options={tableOptionsAddAuc}
    />
  );

  const listTable = (
    <DataTable
      columns={columnsLink}
      data={paginatedRelatedData}
      pagination={relatedPaginationProps}
      storageKey={STORAGE_KEY_LINKED_AUCTION}
      options={tableOptionsLinkedAuc}
    />
  );

  if (
    listGridTitle != null &&
    setIsAddMode != null &&
    searchInput != null
  ) {
    return (
      <RelatedLinksListGridLayout
        storageKey={STORAGE_KEY_LINKED_AUCTION}
        title={listGridTitle}
        backLabel={layoutBackLabel}
        isAddMode={isAddMode}
        onBack={() => setIsAddMode(false)}
        onToggleAdd={() => setIsAddMode(true)}
        searchInput={searchInput}
        addModeTable={addModeTable}
        listTable={listTable}
        gridContent={
          <>
            {paginatedRelatedData.map((row) => (
              <div key={row.id}>
                <GridCardWithActions actions={renderRowActions?.(row)}>
                  <ArtworkLinkGridCard
                    artwork={row.artwork}
                    t={t}
                    onTextClick={() => setSelectedRelationId(row.id)}
                  />
                </GridCardWithActions>
                <ArtworkInAuctionDrawer
                  fields={fields}
                  data={row}
                  queryId={id}
                  type="auction"
                  open={selectedRelationId === row.id}
                  onOpenChange={(open) => {
                    if (!open) setSelectedRelationId(null);
                  }}
                />
              </div>
            ))}
          </>
        }
        relatedPagination={relatedPaginationProps}
      />
    );
  }

  return isAddMode ? addModeTable : listTable;
};
export default ArtworkInAuctionLinks;
